import { Link } from '@tanstack/react-router'
import { Avatar, AvatarFallback } from '@workspace/ui/components/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu'
import type { LucideIcon } from 'lucide-react'
import { LogOut, MessageSquarePlus, Settings, User } from 'lucide-react'

export interface UserMenuLink {
  icon: LucideIcon
  label: string
  to: string
  /** Render as a plain external anchor (opens in a new tab) instead of a router Link. */
  external?: boolean
}

export interface UserMenuProps {
  initials: string
  name: string
  email: string
  avatarClassName?: string
  profileTo?: string
  settingsTo?: string
  extraLinks?: UserMenuLink[]
  onSignOut?: () => void
  onSendFeedback?: () => void
}

export function UserMenu({
  initials,
  name,
  email,
  avatarClassName = 'bg-elevated text-xs font-medium text-white',
  profileTo,
  settingsTo,
  extraLinks,
  onSignOut,
  onSendFeedback,
}: UserMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded-full outline-none ring-offset-background transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Avatar className="size-8">
            <AvatarFallback className={avatarClassName}>{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-48">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium text-foreground">{name}</p>
          <p className="text-xs text-muted-foreground">{email}</p>
        </div>
        <DropdownMenuSeparator />
        {profileTo && (
          <DropdownMenuItem asChild>
            <Link to={profileTo} className="gap-2">
              <User className="size-4" />
              Profile
            </Link>
          </DropdownMenuItem>
        )}
        {settingsTo && (
          <DropdownMenuItem asChild>
            <Link to={settingsTo} className="gap-2">
              <Settings className="size-4" />
              Preferences
            </Link>
          </DropdownMenuItem>
        )}
        {extraLinks && extraLinks.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {extraLinks.map((link) => (
              <DropdownMenuItem key={link.to} asChild>
                {link.external ? (
                  <a href={link.to} target="_blank" rel="noopener noreferrer" className="gap-2">
                    <link.icon className="size-4" />
                    {link.label}
                  </a>
                ) : (
                  <Link to={link.to} className="gap-2">
                    <link.icon className="size-4" />
                    {link.label}
                  </Link>
                )}
              </DropdownMenuItem>
            ))}
          </>
        )}
        {(profileTo || settingsTo || extraLinks?.length) && <DropdownMenuSeparator />}
        <DropdownMenuItem className="gap-2" onSelect={onSendFeedback}>
          <MessageSquarePlus className="size-4" />
          Send Feedback
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="gap-2 text-red-400 focus:text-red-400" onSelect={onSignOut}>
          <LogOut className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
